import React from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Box, Typography, Divider } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

interface ShortcutsDialogProps {
  open: boolean;
  onClose: () => void;
}

const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform);
const mod = isMac ? '⌘' : 'Ctrl';

const GROUPS: { title: string; items: { keys: string[]; label: string }[] }[] = [
  {
    title: 'Global',
    items: [
      { keys: [mod, 'K'], label: 'Open command palette' },
      { keys: [mod, 'J'], label: 'Toggle AI stylist chat' },
      { keys: ['/'], label: 'Quick search (command palette)' },
      { keys: ['?'], label: 'Show this shortcut guide' },
      { keys: ['Esc'], label: 'Close dialogs and overlays' },
    ],
  },
  {
    title: 'Jump to page (press g, then…)',
    items: [
      { keys: ['g', 'd'], label: 'Dashboard' },
      { keys: ['g', 'w'], label: 'Wardrobe' },
      { keys: ['g', 'u'], label: 'Upload' },
      { keys: ['g', 'o'], label: 'Outfits' },
      { keys: ['g', 'c'], label: 'Conversations' },
      { keys: ['g', 's'], label: 'Search' },
      { keys: ['g', 'r'], label: 'Recommendations' },
      { keys: ['g', 'a'], label: 'Activity feed' },
      { keys: ['g', 'p'], label: 'Profile' },
    ],
  },
  {
    title: 'In lists',
    items: [
      { keys: ['↑', '↓'], label: 'Navigate items' },
      { keys: ['↵'], label: 'Select / open' },
    ],
  },
];

const KeyChip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="kbd"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 22,
      height: 22,
      px: 0.75,
      fontFamily: 'Montserrat, sans-serif',
      fontSize: '0.72rem',
      fontWeight: 600,
      backgroundColor: 'background.paper',
      border: '1px solid rgba(0,0,0,0.15)',
      borderBottomWidth: 2,
      color: 'text.primary',
    }}
  >
    {children}
  </Box>
);

const ShortcutsDialog: React.FC<ShortcutsDialogProps> = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
      <Box>
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>
          Speed things up
        </Typography>
        <Typography variant="h5" sx={{ mt: 0.5 }}>
          Keyboard Shortcuts
        </Typography>
      </Box>
      <IconButton onClick={onClose} aria-label="Close">
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent dividers>
      {GROUPS.map((group, gi) => (
        <Box key={group.title} sx={{ mb: gi === GROUPS.length - 1 ? 0 : 2.5 }}>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {group.title}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {group.items.map((item) => (
              <Box
                key={item.label}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 0.5,
                }}
              >
                <Typography variant="body2" sx={{ fontFamily: 'Lato, sans-serif' }}>
                  {item.label}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  {item.keys.map((k, i) => (
                    <React.Fragment key={i}>
                      <KeyChip>{k}</KeyChip>
                      {i < item.keys.length - 1 && (
                        <Typography variant="caption" color="text.secondary">
                          then
                        </Typography>
                      )}
                    </React.Fragment>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
          {gi < GROUPS.length - 1 && <Divider sx={{ mt: 2 }} />}
        </Box>
      ))}
    </DialogContent>
  </Dialog>
);

export default ShortcutsDialog;
